import { useEffect, useMemo, useState } from "react";


import {
  Grid,
  TextField as MUITextField,
  Skeleton,
  Typography,
} from "@mui/material";
import MuiAutocomplete from "@mui/material/Autocomplete";
import match from "autosuggest-highlight/match";
import parse from "autosuggest-highlight/parse";
import { AxiosResponse } from "axios";
import {
  AutocompleteProps,
  AutocompleteRenderInputParams,
  fieldToAutocomplete,
  fieldToTextField,
} from "formik-mui";
import debounce from "lodash/debounce";

import { axios } from "../api";

import type { AeronetV6ItemsResponse } from "../types";
import type { IPartList } from "./types";
import { useExchangeRates } from "../hooks/useExchangeRates";
import { useGlobalConfig } from "../hooks/useGlobalConfig";

export function PartAutocomplete<
  // eslint-disable-next-line
  T,
  Multiple extends boolean | undefined,
  DisableClearable extends boolean | undefined,
  FreeSolo extends boolean | undefined
>(props: AutocompleteProps<IPartList, Multiple, DisableClearable, FreeSolo>) {
  const { form } = props;
  const { setFieldValue } = form;
  const [parts, setParts] = useState<Array<any>>([]);
  const [inputValue, setInputValue] = useState("");
  const { name } = fieldToTextField(props as any);
  const exchangeRates = useExchangeRates();
  const globalConfig = useGlobalConfig();
  const costRounding = Number(globalConfig?.cost_rounding ?? 2);
  const valueRounding = Number(globalConfig?.value_rounding ?? 2);

  const fetchParts = useMemo(
    () =>
      debounce((value: string, callback: Function) => {
        axios
          .get("/v6/parts", {
            params: {
              q: value,
              page: 1,
              size: 15,
              filter: JSON.stringify({
                field: "active",
                op: "==",
                value: true,
              }),
            },
          })
          .then((resp: AxiosResponse<AeronetV6ItemsResponse<IPartList>>) => {
            callback(resp.data.items);
          })
          .catch((_error) => {
            callback([]);
          });
      }, 300),
    []
  );

  useEffect(() => {
    let active = true;

    fetchParts(inputValue, (results: ReadonlyArray<IPartList>) => {
      if (active) {
        let newParts: Array<string | IPartList> = [];

        if (results) {
          newParts = [...newParts, ...results];
        }

        setParts(newParts);
      }
    });

    return () => {
      active = false;
      fetchParts.cancel();
    };
  }, [inputValue, fetchParts]);

  return (
    <>
      {exchangeRates ? (
        <MuiAutocomplete
          {...fieldToAutocomplete(props)}
          fullWidth
          options={parts}
          autoComplete
          includeInputInList
          // API already filters (incl. scrubbed part numbers); skip MUI client filter
          filterOptions={(x) => x}
          //@ts-ignore
          onChange={(_event, newValue: IPartList) => {
            const part = newValue;
            setFieldValue("part", part);
            if (typeof part === "string") {
              setFieldValue("part_id", part);
              setFieldValue("lines.0.part_id", part);
            } else {
              setFieldValue("part_id", part && part.id ? part.id : "");
              setFieldValue("lines.0.part_id", part && part.id ? part.id : "");
              setFieldValue("lines.0.lead_time_text", part && part.lead_time_text ? part.lead_time_text : "None");
              setFieldValue("uom", part?.uom || "");
            }
            if (part && typeof part !== "string") {
              setFieldValue("buy", {
                name: `${Number(part?.list_cost).toFixed(costRounding)} ${part?.list_cost_currency ?? ""}`,
                value: part?.list_cost?.toString() || "",
              });
              setFieldValue("sell", {
                name: `${Number(part?.list_price).toFixed(valueRounding)} ${part?.list_price_currency ?? ""}`,
                value: part?.list_price?.toString() || "",
              });
              if (part.list_cost_currency) {
                setFieldValue("currency_code", part.list_cost_currency);
                setFieldValue(
                  "exchange",
                  exchangeRates[part.list_cost_currency]
                );
              }
            }

            // Call optional external onChange handler if provided
            if (typeof props.onChange === "function") {
              // Pass through the same event and value; ignore extra args
              (props.onChange as any)(_event, newValue);
            }
          }}
          onInputChange={(_event, newInputValue) => {
            setInputValue(newInputValue);
          }}
          getOptionLabel={(option) => {
            return typeof option === "string" ? option : (option.number ?? '')
          }
          }
          isOptionEqualToValue={(option, value) =>
            option.number === value.number
          }
          renderInput={(params: AutocompleteRenderInputParams) => (
            <MUITextField
              {...params}
              label={(props as { label?: string }).label ?? "Part Number"}
              error={props.form.touched[name!] && !!props.form.errors[name!]}
              // @ts-ignore
              helperText={props.form.touched[name!] && props.form.errors[name!]}
              fullWidth
            />
          )}
          renderOption={(props, option, state) => {
            const matches = match(option.number, state.inputValue);
            const parts = parse(option.number, matches);

            return (
              <li {...props}>
                <Grid container alignItems="center">
                  
                  <Grid item xs>
                    {parts.map((part, index) => (
                      <span
                        key={index}
                        style={{ fontWeight: part.highlight ? 700 : 400 }}
                      >
                        {part.text}
                      </span>
                    ))}
                    <Typography variant="body2" color="textSecondary">
                      {option.description} ({option.id})
                    </Typography>
                  </Grid>
                </Grid>
              </li>
            );
          }}
        />
      ) : (
        <Skeleton animation="wave" sx={{ fontSize: "2rem" }} />
      )}
    </>
  );
}

PartAutocomplete.displayName = "FormikMaterialUIPartAutocomplete";
